// CE Timeline Viewer — per-site timeline list with event detail.
import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api, { errorDetail } from '../services/api';

function StaleBadge() {
  return (
    <span className="px-1.5 py-0.5 text-xs rounded border border-amber-700 bg-amber-900/50 text-amber-300 font-mono">
      Evidence Stale — Rebuild Required
    </span>
  );
}

function LayerBadge({ layer }) {
  const isObserved = layer === 'observed';
  return (
    <span
      className={`px-1.5 py-0.5 text-xs rounded border font-mono shrink-0 ${
        isObserved
          ? 'bg-blue-900/60 text-blue-300 border-blue-700'
          : 'bg-emerald-900/60 text-emerald-300 border-emerald-700'
      }`}
    >
      {isObserved ? 'Observed' : 'Derived'}
    </span>
  );
}

function TimelineRow({ tl, expanded, onToggle }) {
  return (
    <div className="border-b border-gray-700/60 last:border-0">
      <button
        className="w-full flex items-start gap-3 py-3 px-1 text-left hover:bg-gray-750 transition-colors"
        onClick={() => onToggle(tl.id)}
      >
        <span className="text-gray-500 text-xs mt-0.5 w-4">{expanded ? '▼' : '▶'}</span>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-0.5">
            <span className="text-sm font-medium text-gray-100">{tl.timeline_type}</span>
            <span className="text-xs font-mono text-gray-500">{tl.id}</span>
            {tl.stale && <StaleBadge />}
          </div>
          <div className="flex flex-wrap gap-3 text-xs text-gray-500">
            <span>{tl.event_count.toLocaleString()} total timeline items (observed + derived)</span>
            <span>{new Date(tl.start_at).toLocaleString()}</span>
          </div>
        </div>
      </button>
      {expanded && tl._loading && (
        <p className="ml-7 pb-3 text-xs text-gray-500">Loading events...</p>
      )}
      {expanded && tl._error && (
        <p className="ml-7 pb-3 text-xs text-red-400">{tl._error}</p>
      )}
      {expanded && tl._detail && (
        <div className="ml-7 pb-3">
          {tl._detail.events && tl._detail.events.length > 0 ? (
            <div className="space-y-1">
              {tl._detail.events.map((ev, i) => (
                <div key={i} className="flex items-start gap-2 text-xs">
                  <span className="text-gray-600 font-mono w-5 shrink-0">{i + 1}</span>
                  <LayerBadge layer={ev.layer} />
                  <span className="text-gray-400 font-mono shrink-0">
                    {ev.timestamp ? new Date(ev.timestamp).toLocaleTimeString() : '—'}
                  </span>
                  <span className="text-gray-300">{ev.event_type}</span>
                </div>
              ))}
              {tl._detail.event_count > tl._detail.events_returned && (
                <p className="text-xs text-gray-600 pt-1">
                  Showing {tl._detail.events_returned} of {tl._detail.event_count} events.
                </p>
              )}
            </div>
          ) : (
            <p className="text-xs text-gray-500">No events attached to this timeline.</p>
          )}
        </div>
      )}
    </div>
  );
}

export default function CETimelineViewer() {
  const [searchParams] = useSearchParams();
  const initSite = searchParams.get('site') || '';

  const [sites, setSites] = useState([]);
  const [siteId, setSiteId] = useState(initSite);
  const [timelines, setTimelines] = useState([]);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [startAt, setStartAt] = useState('');
  const [endAt, setEndAt] = useState('');

  useEffect(() => {
    api.get('/ce/sites').then(r => setSites(r.data.sites || [])).catch(() => {});
  }, []);

  const loadTimelines = useCallback(async () => {
    if (!siteId) { setTimelines([]); return; }
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: 50 });
      if (startAt) params.set('start_at', new Date(startAt).toISOString());
      if (endAt) params.set('end_at', new Date(endAt).toISOString());
      const { data } = await api.get(`/ce/sites/${siteId}/timelines?${params}`);
      setTimelines((data.timelines || []).map(t => ({ ...t, _detail: null, _loading: false })));
      setExpandedIds(new Set());
    } catch (err) {
      setError(errorDetail(err, 'Failed to load timelines'));
    } finally {
      setLoading(false);
    }
  }, [siteId, startAt, endAt]);

  useEffect(() => { loadTimelines(); }, [loadTimelines]);

  const handleToggle = useCallback(async (id) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); return next; }
      next.add(id);
      return next;
    });

    const tl = timelines.find(t => t.id === id);
    if (!tl || tl._detail || tl._loading) return;

    setTimelines(prev => prev.map(t => t.id === id ? { ...t, _loading: true, _error: null } : t));
    try {
      const { data } = await api.get(`/ce/timelines/${id}`);
      setTimelines(prev => prev.map(t => t.id === id ? { ...t, _loading: false, _detail: data } : t));
    } catch (err) {
      setTimelines(prev => prev.map(t =>
        t.id === id ? { ...t, _loading: false, _error: errorDetail(err, 'Failed to load timeline events') } : t
      ));
    }
  }, [timelines]);

  const siteName = sites.find(s => s.id === siteId)?.site_name || siteId;

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100 p-6">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-2xl font-bold text-white mb-1">Timeline Viewer</h1>
        <p className="text-sm text-gray-400 mb-6">Ordered event sequences derived from raw observations</p>

        {/* Filters */}
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-4 mb-6 flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Site</label>
            <select
              value={siteId}
              onChange={e => setSiteId(e.target.value)}
              className="bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="">Select site</option>
              {sites.map(s => (
                <option key={s.id} value={s.id}>{s.site_name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Start at</label>
            <input
              type="datetime-local"
              value={startAt}
              onChange={e => setStartAt(e.target.value)}
              className="bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">End at</label>
            <input
              type="datetime-local"
              value={endAt}
              onChange={e => setEndAt(e.target.value)}
              className="bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <button
            onClick={loadTimelines}
            disabled={loading || !siteId}
            className="px-4 py-2 text-sm rounded-lg bg-blue-700 hover:bg-blue-600 disabled:opacity-50 text-white transition-colors"
          >
            {loading ? 'Loading...' : 'Load'}
          </button>
        </div>

        {!siteId && (
          <p className="text-sm text-gray-500 text-center py-12">Select a site to view timelines.</p>
        )}

        {error && (
          <div className="bg-red-900/40 border border-red-700 rounded-lg px-4 py-3 text-sm text-red-300 mb-4">
            {error}
          </div>
        )}

        {siteId && !loading && timelines.length === 0 && !error && (
          <p className="text-sm text-gray-500 text-center py-12">
            No timelines found for {siteName}.
            Run derivation to build timelines from observed events.
          </p>
        )}

        {timelines.length > 0 && (
          <div className="bg-gray-800 rounded-xl border border-gray-700 px-4">
            <div className="flex items-center justify-between py-3 border-b border-gray-700">
              <span className="text-sm font-medium text-gray-300">
                {timelines.length} timeline{timelines.length !== 1 ? 's' : ''} — {siteName}
              </span>
              {timelines.some(t => t.stale) && (
                <span className="text-xs text-amber-400">
                  {timelines.filter(t => t.stale).length} stale
                </span>
              )}
            </div>
            {timelines.map(tl => (
              <TimelineRow
                key={tl.id}
                tl={tl}
                expanded={expandedIds.has(tl.id)}
                onToggle={handleToggle}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
